import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';

// NETWORKING
import useNewCustomerReportRequest from '../../requests/NewCustomerReportRequest';

// MESSAGES
import NewCustomerReportFormattedMessages from '../../messages/billing/NewCustomerReportFormattedMessages';

import Loader from '../../components/common/Loader';
import Layout from '../../components/layouts/Layout';
import { BillingRoutes } from '../../routes/BillingRoutes';
import MonthYearSelector from '../../components/common/MonthYearSelector';
import NewCustomerTableRow from '../../components/billing/newcustomer/NewCustomerTableRow';
import CancelledCustomerTableRow from '../../components/billing/newcustomer/CancelledCustomerTableRow';
import NewCustomerTable from '../../components/billing/newcustomer/NewCustomerTable';
import CancelledCustomerTable from '../../components/billing/newcustomer/CancelledCustomerTable';

const NewCustomerReport = ({
  customer,
  errorHandler,
  accessLevel,
  accessType,
}) => {
  const currentDate = new Date();
  const monthToQuery = currentDate.getMonth() + 1;
  const yearToQuery = currentDate.getFullYear();
  const { getNewCustomerData } = useNewCustomerReportRequest();

  const [listNewCustomers, setListNewCustomers] = useState(null);
  const [listCancelledCustomers, setListCancelledCustomers] = useState(null);
  const [dataNewCustomer, setDataNewCustomer] = useState(null);
  const [dataCancelledCustomers, setDataCancelledCustomers] = useState(null);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageCount, setPageCount] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(monthToQuery);
  const [selectedYear, setSelectedYear] = useState(yearToQuery);

  const getReportData = useCallback(async () => {
    setLoading(true);
    const customerData = await getNewCustomerData(
      selectedMonth,
      selectedYear,
      errorHandler
    );
    setDataNewCustomer(customerData.new);
    setDataCancelledCustomers(customerData.cancelled);
    setTotals({
      Total: 0,
      Count: customerData.new?.length,
      Cancelled: customerData.cancelled?.length,
      totalUsers: 0,
    });
  }, [customer, selectedMonth, selectedYear]);

  useEffect(() => {
    if (customer && customer !== null) {
      getReportData();
    }
  }, [getReportData]);

  /**
   * Sorts list of customers by created_date
   * @param {Array} data Array of customers (either new or cancelled)
   * @returns Sorted list
   */
  const sortList = (data) => {
    return data.sort(function (a, b) {
      if (a.created_date < b.created_date) {
        return -1;
      }
      if (a.created_date > b.created_date) {
        return 1;
      }
      return 0;
    });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'selectedMonth') {
      setSelectedMonth(Number(value));
    } else if (name === 'selectedYear') {
      setSelectedYear(Number(value));
    }
  };

  /*
    Formats the data in a manner required for the Table
    to properly display the data
  */
  const formatData = useCallback(() => {
    let totals = {
      Total: 0,
      Count: dataNewCustomer?.length,
      Cancelled: dataCancelledCustomers?.length,
      totalUsers: 0,
    };

    const newCustomerList = dataNewCustomer.map((el) => {
      const formatDate = DateTime.fromISO(el.created_date).toFormat(
        'dd-MMM-yy'
      );
      return (
        <NewCustomerTableRow
          key={el.customer_id}
          id={el.customer_id}
          createdDate={formatDate}
          date={el.status_date}
          customerID={el.customer_friendly_id}
          customerName={el.customer_name}
          customerType={el.customer_type}
          customerLocation={el.country_name}
          agent={el.sales_agent_name}
          minCommitment={el.minimum_commitment}
          alreadyBilled={el.alreadyBilled}
        />
      );
    });

    dataNewCustomer.forEach((el) => {
      totals.Total = totals.Total + parseInt(el.minimum_commitment);
    });

    const cancelledCustomerList = dataCancelledCustomers.map((el) => {
      const formatDate = DateTime.fromISO(el.created_date).toFormat(
        'dd-MMM-yy'
      );
      totals.totalUsers += el.numberOfUsers;
      return (
        <CancelledCustomerTableRow
          key={el.customer_id}
          id={el.customer_id}
          createdDate={formatDate}
          date={el.status_date}
          customerID={el.customer_friendly_id}
          customerName={el.customer_name}
          customerType={el.customer_type}
          customerLocation={el.country_name}
          agent={el.sales_agent_name}
          numberOfUsers={el.numberOfUsers}
        />
      );
    });

    setListNewCustomers(newCustomerList);
    setListCancelledCustomers(cancelledCustomerList);
    setTotals(totals);
    setLoading(false);
  }, [dataNewCustomer, dataCancelledCustomers]);

  useEffect(() => {
    if (dataNewCustomer || dataCancelledCustomers) {
      formatData();
    }
  }, [formatData]);

  if (accessLevel === '3' && accessType === '3') {
    return (
      <Layout
        routes={BillingRoutes}
        title={NewCustomerReportFormattedMessages.newCustomerReport}
        accessLevel={accessLevel}
        accessType={accessType}
        customerType={customer.customer_type}
        backgroundColor="none"
        titleRowRightComponent={
          <Box
            sx={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}
          >
            <MonthYearSelector
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              handleInputChange={handleInputChange}
            />
          </Box>
        }
      >
        {loading ? (
          <Loader />
        ) : (
          <Stack sx={{ gap: 4 }}>
            <Box>
              <Typography variant="h2" gutterBottom>
                {NewCustomerReportFormattedMessages.newCustomers}
              </Typography>

              <NewCustomerTable
                changePage={() => {}}
                totals={totals}
                listNewCustomers={listNewCustomers}
                handlePagination={() => {}}
                pageCount={pageCount}
              />
            </Box>

            <Box>
              <Typography variant="h2" gutterBottom>
                {NewCustomerReportFormattedMessages.cancelledCustomers}
              </Typography>

              <CancelledCustomerTable
                changePage={() => {}}
                totals={totals}
                listCancelledCustomers={listCancelledCustomers}
                handlePagination={() => {}}
                pageCount={pageCount}
              />
            </Box>
          </Stack>
        )}
      </Layout>
    );
  }
  return null;
};

export default NewCustomerReport;
